import React, { useState, useEffect } from 'react';
import { Database, Trash2, RefreshCw, Settings, AlertTriangle } from 'lucide-react';
import OrganisationNavigation from '../OrganisationNavigation';

interface CacheItem {
  id: string;
  name: string;
  size: string;
  lastUpdated: string;
  status: 'active' | 'expired' | 'error';
}

const OrgChartCacheManagement: React.FC = () => {
  const [cacheItems, setCacheItems] = useState<CacheItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadCacheData = async () => {
      setLoading(true);
      try {
        // Simulation des données de cache
        const mockCacheData: CacheItem[] = [
          {
            id: '1',
            name: 'Organigramme Principal',
            size: '2.3 MB',
            lastUpdated: '2024-01-15 14:30',
            status: 'active'
          },
          {
            id: '2',
            name: 'Cache Employés',
            size: '1.8 MB',
            lastUpdated: '2024-01-15 12:15',
            status: 'active'
          },
          {
            id: '3',
            name: 'Cache Départements',
            size: '0.9 MB',
            lastUpdated: '2024-01-14 16:45',
            status: 'expired'
          },
          {
            id: '4',
            name: 'Cache Hiérarchie',
            size: '3.2 MB',
            lastUpdated: '2024-01-15 09:20',
            status: 'error'
          }
        ];
        
        setCacheItems(mockCacheData);
        setLoading(false);
      } catch (error) {
        console.error('Erreur lors du chargement du cache:', error);
        setLoading(false);
      }
    };

    loadCacheData();
  }, []);

  const handleClearCache = (id: string) => {
    setCacheItems(prev => prev.filter(item => item.id !== id));
  };

  const handleClearAllCache = () => {
    setCacheItems([]);
  };

  const handleRefreshCache = (id: string) => {
    setCacheItems(prev => prev.map(item => 
      item.id === id 
        ? { ...item, lastUpdated: new Date().toLocaleString('fr-FR'), status: 'active' as const }
        : item
    ));
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'expired':
        return 'bg-yellow-100 text-yellow-800';
      case 'error':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active':
        return 'Actif';
      case 'expired':
        return 'Expiré';
      case 'error':
        return 'Erreur';
      default:
        return 'Inconnu';
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <OrganisationNavigation />
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Chargement du cache...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <OrganisationNavigation />
      
      {/* En-tête */}
      <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Database className="h-6 w-6 text-blue-600" />
              Gestion du Cache
            </h2>
            <p className="text-gray-600 mt-1">
              Gestion du cache de l'organigramme et des données organisationnelles
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <button 
              onClick={handleClearAllCache}
              className="btn-danger flex items-center space-x-2"
            >
              <Trash2 className="h-4 w-4" />
              <span>Vider tout le cache</span>
            </button>
            <button className="btn-primary flex items-center space-x-2">
              <Settings className="h-4 w-4" />
              <span>Configuration</span>
            </button>
          </div>
        </div>
      </div>

      {/* Liste des éléments de cache */}
      <div className="bg-white rounded-lg shadow border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Éléments en cache</h3>
        </div>
        <div className="divide-y divide-gray-200">
          {cacheItems.length === 0 ? (
            <div className="p-6 text-center">
              <Database className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">Aucun élément en cache</p>
            </div>
          ) : (
            cacheItems.map((item) => (
              <div key={item.id} className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <h4 className="text-sm font-medium text-gray-900">{item.name}</h4>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(item.status)}`}>
                        {item.status === 'error' && <AlertTriangle className="h-3 w-3 mr-1" />}
                        {getStatusText(item.status)}
                      </span>
                    </div>
                    <div className="mt-1 flex items-center space-x-4 text-sm text-gray-500">
                      <span>Taille: {item.size}</span>
                      <span>Dernière mise à jour: {item.lastUpdated}</span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleRefreshCache(item.id)}
                      className="btn-secondary flex items-center space-x-1"
                    >
                      <RefreshCw className="h-4 w-4" />
                      <span>Actualiser</span>
                    </button>
                    <button
                      onClick={() => handleClearCache(item.id)}
                      className="btn-danger flex items-center space-x-1"
                    >
                      <Trash2 className="h-4 w-4" />
                      <span>Supprimer</span>
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default OrgChartCacheManagement;
